import type { OutsideResetBurstAnimationMode } from "../core/hoverIdleMotionPolicy";

/**
 * Decides, frame by frame, whether the Outside Burst effect should play.
 *
 * It tracks the snapshot's Outside-reset serial and reports "play" only when
 * the serial changed since the previous frame, the player is on the Hov target
 * screen, and the burst is enabled. The serial is always consumed, so an
 * Outside reset that happened off-screen or while disabled is never replayed
 * later.
 */
export type OutsideResetBurstDecision = "play" | "skip";

export interface OutsideResetBurstEvaluateInput {
  /** Monotonic Outside-reset marker from the current snapshot. */
  readonly serial: number;
  /** Resolved burst mode from the motion policy. */
  readonly mode: OutsideResetBurstAnimationMode;
  /** Whether the Hov target screen is currently visible. */
  readonly isHovTargetScreenActive: boolean;
}

export class OutsideResetBurstScheduler {
  private lastSerial: number | null = null;

  /**
   * Consumes the current serial and returns whether to play a burst now.
   * The first call only initializes the baseline and returns "skip", so a
   * fresh load never replays historical OHov executions.
   */
  evaluate(input: OutsideResetBurstEvaluateInput): OutsideResetBurstDecision {
    const previousSerial = this.lastSerial;
    this.lastSerial = input.serial;

    if (previousSerial === null || input.serial === previousSerial) {
      return "skip";
    }
    if (input.mode === "off" || !input.isHovTargetScreenActive) {
      return "skip";
    }
    return "play";
  }
}
